import { ILogger } from '../support/ILogger';
import { Logger } from '../support/Logger';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { UploadsAwaitingLinks } from './UploadsAwaitingLinks';
import { UploadsLocation } from './UploadsLocation';

/**
 * Refuses to read a care map photo we host without the audited link the api sends with it.
 *
 * The api offers a care map photo link only for a url in our uploads bucket, so a hosted photo
 * arriving with no link means a caller dropped it, and reading the stored url instead would leave no
 * record of who saw the face. A photo we do not host, such as a Google account picture, has no link
 * by design and is read as stored, and so is a photo this device put on a record that has not come
 * back with its link yet. An app user's own picture has no audited route, so its callers opt out.
 */
export class HostedPhotoGuard {
    static inject = () => [UploadsLocation, UploadsAwaitingLinks, Logger, HistoryViewModel];
    constructor(
        private uploads: UploadsLocation,
        private uploadsAwaitingLinks: Pick<UploadsAwaitingLinks, 'has'>,
        private logger: ILogger,
        private history: Pick<HistoryViewModel, 'getCurrentLocation'>
    ) {}

    private reported = new Set<string>();

    refuses = (params: { photo?: string; photoHref?: string }): boolean => {
        const { photo, photoHref } = params;
        const uploadsBaseUrl = this.uploads.baseUrl;

        // A blank base url would make startsWith match every photo.
        if (!photo || photoHref || !uploadsBaseUrl) {
            return false;
        }
        return photo.startsWith(uploadsBaseUrl) && !this.uploadsAwaitingLinks.has(photo);
    };

    // A refused photo is usually drawn wherever its person appears, so each is reported once. The
    // report names the screen rather than the photo: its url opens the face with no audit record.
    reportRefused = (photo: string): void => {
        if (this.reported.has(photo)) {
            return;
        }
        this.reported.add(photo);
        this.logger.error(new Error('refused to read a hosted photo that arrived without its audited link'), { route: this.history.getCurrentLocation().pathname });
    };
}
